import { Heading } from '@efdigital/ll-addon-ui';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface LibraryFoldersNewModel {
  folder: { name: string; parent: string; id?: string };
  parent: string;
}

export default class LibraryFoldersNew extends Component<{
  Args: { model: LibraryFoldersNewModel };
}> {
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked errors: string[] = [];
  @tracked isSaving = false;

  onSubmit = async (event: Event) => {
    event.preventDefault();
    this.errors = [];
    this.isSaving = true;

    try {
      await this.store.request({
        op: 'createRecord',
        data: {
          type: 'folders',
          attributes: {
            name: this.args.model.folder.name,
            parent: this.args.model.folder.parent,
          },
        },
      });

      this.router.transitionTo('library.folders.index', {
        queryParams: { parent: this.args.model.parent },
      });
    } catch (error) {
      this.errors = [(error as Error).message];
    } finally {
      this.isSaving = false;
    }
  };

  <template>
    <div class="container py-5">
      <Heading @text="New Folder">
        <LinkTo
          @route="library.folders.index"
          @query={{hash parent=@model.parent}}
          class="btn btn-sm btn-outline-secondary"
        >
          <iconify-icon inline icon="ph:arrow-left" />
          Back to Folders
        </LinkTo>
      </Heading>

      <div class="card">
        <div class="card-body">
          <form {{on "submit" this.onSubmit}}>
            <div class="mb-3">
              <label for="folder-name" class="form-label">Folder Name</label>
              <input
                type="text"
                id="folder-name"
                class="form-control"
                value={{@model.folder.name}}
                {{on "input" (fn (mut @model.folder.name) target="value")}}
                required
                placeholder="Enter folder name"
              />
            </div>

            <div class="mb-3">
              <label for="folder-parent" class="form-label">Parent</label>
              <input
                type="text"
                id="folder-parent"
                class="form-control"
                value={{@model.folder.parent}}
                readonly
                disabled
              />
            </div>

            {{#if this.errors.length}}
              <div class="alert alert-danger">
                {{#each this.errors as |error|}}
                  <p>{{error}}</p>
                {{/each}}
              </div>
            {{/if}}

            <div class="d-flex gap-2">
              <button
                type="submit"
                class="btn btn-primary"
                disabled={{this.isSaving}}
              >
                {{#if this.isSaving}}
                  <span class="spinner-border spinner-border-sm me-2"></span>
                {{/if}}
                Create Folder
              </button>
              <LinkTo
                @route="library.folders.index"
                @query={{hash parent=@model.parent}}
                class="btn btn-outline-secondary"
              >
                Cancel
              </LinkTo>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>
}
